/**
 * Application shell — masthead, tab navigation, and the state shared across
 * tabs (contacts, lists, session history, call outcomes). Reads deployment
 * settings from Vite env vars so the same bundle can point at a local
 * backend or a deployed one.
 *
 * Contacts, Lists and Session History are persisted server-side (see
 * workspaceStore.js) — they used to live only in this browser's localStorage
 * (AGENTS.md still documents why that was a deliberate scope line, and why
 * it no longer holds). The tabs below are unchanged; only where this state
 * comes from moved.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import DialerDevice, { AgentStatus } from './DialerDevice.jsx';
import ContactsTab from './tabs/ContactsTab.jsx';
import ListsTab from './tabs/ListsTab.jsx';
import ReportsTab from './tabs/ReportsTab.jsx';
import ManualDialPad from './ManualDialPad.jsx';
import { parseContactsCsv, toE164 } from './lib/csv.js';
import { selectCampaignContacts } from './lib/campaignSelection.js';
import { MIN_PASSWORD_LENGTH } from './lib/password.js';

const TABS = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'lists', label: 'Lists' },
  { key: 'reports', label: 'Reports' },
];

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const THEME_STORAGE_KEY = 'a305-theme';

export default function App() {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState([]);

  // Dark is the shipped default (index.html's inline script already applied
  // a stored "light" preference before this ever runs, to avoid a flash of
  // the wrong theme) — this just keeps state and the DOM attribute in sync
  // from here on. Per-viewer convenience, not app data, so plain
  // localStorage is the right place for it, guarded since a private window
  // or blocked site data can make it throw.
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
    } catch {
      return 'dark';
    }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* private window or blocked storage — theme still applies for this load */
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const [lists, setLists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [callOutcomes, setCallOutcomes] = useState([]);
  const [loadRequest, setLoadRequest] = useState(null);
  const [dialerState, setDialerState] = useState({ online: false, busy: false });

  // phone (E.164) → { disposition, at }. Not persisted — it is a live-shift
  // view of "who did we just call", not a call log; the durable record of a
  // call is the HubSpot engagement the backend already writes.
  const [callHistory, setCallHistory] = useState(new Map());

  const identity = import.meta.env.VITE_AGENT_IDENTITY ?? 'agent_1';
  const apiBase = import.meta.env.VITE_API_BASE ?? '';
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [operatorKey, setOperatorKey] = useState('');
  const [authInfo, setAuthInfo] = useState({ accountsEnabled: false, needsSetup: false, user: null });
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [setup, setSetup] = useState({ name: '', email: '', password: '', confirm: '' });
  const [authError, setAuthError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  /** Re-reads who the server thinks we are (and whether accounts are enabled). */
  const refreshAuth = useCallback(async () => {
    const res = await fetch(`${apiBase}/api/auth/session`);
    const body = await res.json();
    setAuthenticated(body.authenticated === true);
    setAuthInfo({ accountsEnabled: body.accountsEnabled === true, needsSetup: body.needsSetup === true, user: body.user ?? null });
    return body;
  }, [apiBase]);

  useEffect(() => {
    refreshAuth()
      .catch(() => setAuthError('Could not reach the dialer server.'))
      .finally(() => setCheckingAuth(false));
  }, [refreshAuth]);

  // With accounts enabled and at least one account created, the login screen
  // takes an email and password; the dashboard key is only for first-time setup.
  const passwordLogin = authInfo.accountsEnabled && !authInfo.needsSetup;
  const setupRequired = authenticated && authInfo.accountsEnabled && authInfo.needsSetup;

  async function signIn(event) {
    event.preventDefault();
    setSigningIn(true);
    setAuthError('');
    try {
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordLogin ? credentials : { key: operatorKey }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body.error === 'key_login_disabled') {
          await refreshAuth().catch(() => {});
          throw new Error('Accounts are set up now — sign in with your email and password.');
        }
        if (res.status === 429) throw new Error('Too many attempts. Try again in 15 minutes.');
        if (res.status === 401) throw new Error(passwordLogin ? 'That email and password do not match an account.' : 'Invalid dashboard key.');
        throw new Error('Could not sign in. Try again in a moment.');
      }
      setOperatorKey('');
      setCredentials({ email: '', password: '' });
      await refreshAuth();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setSigningIn(false);
    }
  }

  async function createOwnerAccount(event) {
    event.preventDefault();
    setSigningIn(true);
    setAuthError('');
    try {
      if (setup.password !== setup.confirm) throw new Error('The two passwords do not match.');
      const res = await fetch(`${apiBase}/api/accounts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: setup.name, email: setup.email, password: setup.password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Could not create the account.');
      setSetup({ name: '', email: '', password: '', confirm: '' });
      await refreshAuth();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setSigningIn(false);
    }
  }

  async function signOut() {
    try {
      const res = await fetch(`${apiBase}/api/sessions`);
      if (!res.ok) throw new Error('Could not check active sessions.');
      const { sessions } = await res.json();
      const active = sessions.filter((entry) => ['DIALING', 'IN_CALL', 'PAUSED'].includes(entry.state));
      if (active.length && !window.confirm('Signing out will stop active dialing and hang up live calls. Continue?')) return;
      for (const entry of active) {
        const stopped = await fetch(`${apiBase}/api/sessions/${encodeURIComponent(entry.sessionId)}`, { method: 'DELETE' });
        if (!stopped.ok) throw new Error('Could not stop the active session.');
      }
      const logout = await fetch(`${apiBase}/api/auth/logout`, { method: 'POST' });
      if (!logout.ok) throw new Error('Could not sign out.');
      setAuthenticated(false);
      refreshAuth().catch(() => {});
      setContacts([]);
      setLists([]);
      setSessionHistory([]);
      setCallOutcomes([]);
      setLoadRequest(null);
      setSelectedListIds([]);
      setSettingsOpen(false);
      setDialerState({ online: false, busy: false });
    } catch (err) {
      window.alert(err.message);
    }
  }

  // Contacts/Lists/Session History all live on the server now; load the
  // snapshot once on mount. A failure here is loud (console only, no retry
  // UI yet) rather than silent — leaving the tabs empty is confusing enough
  // to notice and report, which beats pretending the workspace is empty.
  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    fetch(`${apiBase}/api/workspace/state`)
      .then((res) => {
        if (!res.ok) throw new Error(`workspace state request failed (${res.status})`);
        return res.json();
      })
      .then((state) => {
        if (cancelled) return;
        setContacts(state.contacts ?? []);
        setLists(state.lists ?? []);
        setSessionHistory(state.sessionHistory ?? []);
        setCallOutcomes(state.callOutcomes ?? []);
      })
      .catch((err) => {
        if (!cancelled) console.error('failed to load workspace state', err);
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, authenticated]);

  const handleImport = useCallback(
    async (csvText, name, phone1Column) => {
      const { contacts: parsed } = parseContactsCsv(csvText, { phone1Column });
      const listId = newId();
      const withIds = parsed.map((c) => ({
        ...c,
        id: newId(),
        phone1: toE164(c.phone1),
        phone2: toE164(c.phone2),
        listId,
        listName: name,
      }));
      const dialableCount = withIds.filter((c) => /^\+[1-9]\d{6,14}$/.test(c.phone1)).length;
      const list = { id: listId, name, count: withIds.length, dialableCount, importedAt: Date.now() };

      try {
        const response = await fetch(`${apiBase}/api/workspace/lists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ list, contacts: withIds }),
        });
        if (!response.ok) throw new Error(`import failed (${response.status})`);

        // Only reflected in the UI once the server confirms it persisted —
        // otherwise a failed write would look like a successful import until
        // the next reload silently dropped it.
        setContacts((prev) => [...prev, ...withIds]);
        setLists((prev) => [...prev, list]);
        return true;
      } catch (err) {
        console.error('failed to import list', err);
        alert(`Import failed: ${err.message}. Check the browser console and Railway logs.`);
        return false;
      }
    },
    [apiBase],
  );

  const handleDeleteList = useCallback(
    async (listId) => {
      try {
        const response = await fetch(`${apiBase}/api/workspace/lists/${encodeURIComponent(listId)}`, {
          method: 'DELETE',
        });
        if (!response.ok && response.status !== 404) throw new Error(`delete failed (${response.status})`);

        setLists((prev) => prev.filter((l) => l.id !== listId));
        setContacts((prev) => prev.filter((c) => c.listId !== listId));
        setSelectedListIds((prev) => prev.filter((id) => id !== listId));
      } catch (err) {
        console.error('failed to delete list', err);
      }
    },
    [apiBase],
  );

  const followUpPhones = useMemo(() => {
    const latest = new Map();
    for (const item of callOutcomes) if (item.phone) latest.set(item.phone, item.outcome);
    return new Set([...latest].filter(([, outcome]) => ['callback', 'meaningful', 'email'].includes(outcome)).map(([phone]) => phone));
  }, [callOutcomes]);

  const handleStartCampaign = useCallback(
    async (ids) => {
      const active = await fetch(`${apiBase}/api/sessions`).then((response) => {
        if (!response.ok) throw new Error('Could not check the active session.');
        return response.json();
      }).catch((error) => { window.alert(error.message); return null; });
      if (!active) return;
      if (active.sessions.some((entry) => ['DIALING', 'IN_CALL', 'PAUSED'].includes(entry.state))) {
        window.alert('Stop or finish the active session before loading another list.');
        return;
      }
      const selected = new Set(ids);
      const listContacts = selectCampaignContacts(contacts, ids, callOutcomes, followUpPhones);
      if (!listContacts.length) {
        window.alert('No dialable numbers in the selected lists. Check the phone column in the original CSV and reimport it. Nothing was dialed.');
        return;
      }
      const numbers = listContacts.map((c) => c.phone1).join('\n');
      // The full records ride along too, keyed by phone1 in DialerDevice, so
      // a connected call can screen-pop the company/name instead of just the
      // number — see DialerDevice's `phoneToContact`.
      const listName = selected.has('follow-ups') ? 'Warm follow-ups' : lists.filter((list) => selected.has(list.id)).map((list) => list.name).join(' + ');
      setLoadRequest({ text: numbers, listName, contacts: listContacts, token: Date.now() });
      setActiveTab('campaigns');
    },
    [apiBase, contacts, callOutcomes, followUpPhones, lists],
  );

  const handleManualDial = useCallback(async (phone) => {
    const active = await fetch(`${apiBase}/api/sessions`).then((response) => {
      if (!response.ok) throw new Error('Could not check the active session.');
      return response.json();
    }).catch((error) => { window.alert(error.message); return null; });
    if (!active) return false;
    if (active.sessions.some((entry) => ['DIALING', 'IN_CALL', 'PAUSED'].includes(entry.state))) {
      window.alert('Stop or finish the active session before dialing another number.');
      return false;
    }
    const contact = contacts.find((entry) => entry.phone1 === phone);
    setLoadRequest({ text: phone, listName: 'Quick dial', contacts: contact ? [contact] : [], token: Date.now(), autoStart: true });
    setActiveTab('campaigns');
    return true;
  }, [apiBase, contacts]);

  const handleLegEnded = useCallback(({ phone, disposition, at }) => {
    setCallHistory((prev) => {
      const next = new Map(prev);
      next.set(phone, { disposition, at });
      return next;
    });
  }, []);

  const handleSessionEnded = useCallback(
    (summary) => {
      // Reflected immediately — an agent switching to Reports right after a
      // session shouldn't wait on a network round trip to see it. Persisted
      // in the background; a failure here is logged, not surfaced, since
      // Reports is a convenience view and the durable record of what
      // actually happened on each call is the HubSpot engagement.
      setSessionHistory((prev) => [...prev, summary]);
      setActiveTab('reports');

      fetch(`${apiBase}/api/workspace/session-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(summary),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`save failed (${response.status})`);
        })
        .catch((err) => console.error('failed to persist session history', err));
    },
    [apiBase],
  );

  const dialableCount = useMemo(() => contacts.filter((c) => /^\+[1-9]\d{6,14}$/.test(c.phone1)).length, [contacts]);
  const listDialableCounts = useMemo(() => new Map(lists.map((list) => [list.id,
    contacts.filter((contact) => contact.listId === list.id && /^\+[1-9]\d{6,14}$/.test(contact.phone1)).length,
  ])), [lists, contacts]);

  if (checkingAuth) return <main className="app"><p>Checking operator session…</p></main>;
  if (setupRequired) return (
    <main className="app app--login">
      <form className="operator-login" onSubmit={createOwnerAccount}>
        <span className="operator-login__brand"><span className="operator-login__dot" /> AUTOMATE <em>305</em></span>
        <span className="operator-login__eyebrow">FIRST-TIME SETUP</span>
        <h1>Create your account</h1>
        <p>You are in with the dashboard key. Create your own login now — after this, the key no longer signs anyone in.</p>
        <label className="field">Name
          <input type="text" autoComplete="name" maxLength={80} value={setup.name}
            onChange={(event) => setSetup({ ...setup, name: event.target.value })} />
        </label>
        <label className="field">Email
          <input type="email" autoComplete="username" required value={setup.email}
            onChange={(event) => setSetup({ ...setup, email: event.target.value })} />
        </label>
        <label className="field">Password (12+ characters)
          <input type="password" autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={setup.password}
            onChange={(event) => setSetup({ ...setup, password: event.target.value })} />
        </label>
        <label className="field">Confirm password
          <input type="password" autoComplete="new-password" required value={setup.confirm}
            onChange={(event) => setSetup({ ...setup, confirm: event.target.value })} />
        </label>
        {authError && <p role="alert" className="operator-login__error">{authError}</p>}
        <button className="btn btn--primary btn--block" type="submit" disabled={signingIn}>
          {signingIn ? 'Creating…' : 'Create account'}
        </button>
      </form>
    </main>
  );
  if (!authenticated) return (
    <main className="app app--login">
      <form className="operator-login" onSubmit={signIn}>
        <span className="operator-login__brand"><span className="operator-login__dot" /> AUTOMATE <em>305</em></span>
        <span className="operator-login__eyebrow">PRIVATE OPERATIONS</span>
        <h1>dialer</h1>
        <p>{passwordLogin ? 'Sign in to your calling workspace.'
          : authInfo.accountsEnabled ? 'First-time setup: unlock with the dashboard key, then create your own account.'
            : 'Unlock your calling workspace.'}</p>
        {passwordLogin ? <>
          <label className="field">Email
            <input type="email" autoComplete="username" required value={credentials.email}
              onChange={(event) => setCredentials({ ...credentials, email: event.target.value })} />
          </label>
          <label className="field">Password
            <input type="password" autoComplete="current-password" required value={credentials.password}
              onChange={(event) => setCredentials({ ...credentials, password: event.target.value })} />
          </label>
        </> : (
          <label className="field">Dashboard key
            <input type="password" autoComplete="current-password" required value={operatorKey}
              onChange={(event) => setOperatorKey(event.target.value)} placeholder="Enter dashboard key" />
          </label>
        )}
        {authError && <p role="alert" className="operator-login__error">{authError}</p>}
        <button className="btn btn--primary btn--block" type="submit" disabled={signingIn}>
          {signingIn ? (passwordLogin ? 'Signing in…' : 'Unlocking…') : (passwordLogin ? 'Sign in' : 'Unlock dialer')}
        </button>
      </form>
    </main>
  );

  return (
    <main className="app">
      <div className="shell">
        <header className="dialer__header">
          <div>
            <h1 className="dialer__title">A305 Dialer</h1>
            <p className="dialer__subtitle">Agent workstation · {identity}</p>
          </div>
          <div className="row">
            <span className="pill pill--muted">{dialableCount} dialable contacts</span>
            {authInfo.user && <span className="pill pill--muted" title="Signed in as">{authInfo.user.name || authInfo.user.email}</span>}
            <button type="button" className="btn btn--ghost settings-trigger" aria-label="Agent settings" title="Agent settings"
              onClick={() => { setSettingsOpen(activeTab === 'campaigns' ? !settingsOpen : true); setActiveTab('campaigns'); }}>⚙</button>
            <button type="button" className="btn btn--ghost theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? 'Light theme' : 'Dark theme'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={signOut}>Sign out</button>
          </div>
        </header>

        <nav className="tabs" role="tablist" aria-label="Workstation sections">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`tabs__item ${activeTab === tab.key ? 'tabs__item--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Campaigns stays mounted across tab switches so a live call and its
            Device registration survive the agent checking Reports mid-shift. */}
        <div className="tab-panel" hidden={activeTab !== 'campaigns'}>
          <div className="overview__stats"><div className="panel"><span>Lists</span><strong>{lists.length}</strong></div>
            <div className="panel"><span>Dialable contacts</span><strong>{dialableCount}</strong></div>
            <div className="panel"><span>Warm follow-ups</span><strong>{followUpPhones.size}</strong></div></div>
          <div className="campaign-layout"><DialerDevice
            apiBase={apiBase}
            identity={identity}
            settingsOpen={settingsOpen}
            theme={theme}
            onToggleTheme={toggleTheme}
            currentUser={authInfo.user}
            accountsEnabled={authInfo.accountsEnabled}
            onCloseSettings={() => setSettingsOpen(false)}
            loadRequest={loadRequest}
            contacts={contacts}
            onStateChange={setDialerState}
            onLegEnded={handleLegEnded}
            onSessionEnded={handleSessionEnded}
            onOutcomeSaved={(outcome) => setCallOutcomes((prev) => [...prev, outcome])}
          />
          <aside className="campaign-sidebar"><ListsTab lists={lists} selectedIds={selectedListIds} onSelectionChange={setSelectedListIds}
            dialableCounts={listDialableCounts} onStartCampaign={handleStartCampaign} onDelete={handleDeleteList} compact />
            <ManualDialPad canDial={dialerState.online && !dialerState.busy} busy={dialerState.busy} onDial={handleManualDial} />
            {followUpPhones.size > 0 && <section className="panel"><h3 className="panel__title">Warm follow-ups</h3>
              <p className="idle__text">Latest outcome: Follow up, Meaningful conversation, or Send email.</p>
              <button type="button" className="btn" onClick={() => handleStartCampaign(['follow-ups'])}>Load follow-ups</button></section>}</aside></div>
        </div>

        {activeTab === 'contacts' && (
          <div className="tab-panel">
            <ContactsTab contacts={contacts} lists={lists} onImport={handleImport} callHistory={callHistory} />
          </div>
        )}

        {activeTab === 'lists' && (
          <div className="tab-panel">
            <ListsTab lists={lists} selectedIds={selectedListIds} onSelectionChange={setSelectedListIds}
              dialableCounts={listDialableCounts} onStartCampaign={handleStartCampaign} onDelete={handleDeleteList} />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="tab-panel">
            <ReportsTab sessionHistory={sessionHistory} callOutcomes={callOutcomes} apiBase={apiBase} />
          </div>
        )}
      </div>
    </main>
  );
}

export { AgentStatus };
